import React from 'react';
import { Link } from 'react-router-dom';
import fullworkflow from '../fullworkflow.png';
import emptyworkflow from '../empty.png';
import managestatus from '../statuses.png';
import conditions from '../conditions.png';
import actions from '../actions.png';
import overview from '../Overview.png';
import dialog from '../dialog.png';
import workflowcompare from '../workflows-compare.png';
import workflowicon from '../workflow.svg';
import arrowsicon from '../arrow-up-down.svg';
import checks from '../check-check.svg';
import bolt from '../zap.svg';
import { useScrollToTop } from '../hooks/useScrollToTop';
import './CaseStudy.css';

function CaseStudy2() {
  useScrollToTop();
  return (
    <div className="case-study-page">
      <div className="case-study-container">
        <Link to="/" className="back-button">
          <i className="fas fa-arrow-left"></i>
          <span>Back to all Projects</span>
        </Link>
        
        <div className="case-study-headline">
          <h1>Designing a workflow builder and rules engine to support compliance-friendly construction draw disbursements.</h1>
          <p className="internal-case-study-body">Construction lending is high-touch, high-risk, and high-cost to manage. Nearly 90% of large infrastructure projects exceed their budgets—by an average of 28%—causing widespread financial leakage and delays.</p>
        </div>
        
        <div className="case-study-section">
          <div className="case-study-left-column">
            <div className="info-pair">
              <span className="info-label">Timeline</span>
              <span className="info-text">2023</span>
            </div>
            <div className="info-pair">
              <span className="info-label">Tech Stack</span>
              <span className="info-text">Temporal Workflows, ReactFlow UI</span>
            </div>
            <div className="info-pair">
              <span className="info-label">ICP Targeted</span>
              <span className="info-text">CRE Loan Administrators</span>
            </div>
            <div className="info-pair">
              <span className="info-label">My Role</span>
              <span className="info-text">Product Designer</span>
            </div>
          </div>
          
          <div className="case-study-right-column">
            <div className="section-content">
              <span className="info-label">Background</span>
              <h2>Digitizing Manual Workflows</h2>
              <p className="internal-case-study-body">Construction lenders face significant challenges in managing complex approval workflows while maintaining regulatory compliance. The traditional process relies on manual oversight and paper-based documentation, leading to delays, errors, and increased operational costs.</p>
            </div>
          </div>
        </div>
        
        <div className="margin-top-72 image-container" style={{ width: '100%' }}>
          <img src={fullworkflow} alt="Case Study 2" style={{ width: '100%', height: 'auto' }} />
        </div>

        <div className="margin-top-72 case-study-section-slim">
            <div className="section-content">
              <span className="info-label">Problem to Solve</span>
              <h2>The gap between policy and practice</h2>
              <p className='internal-case-study-body'>When a Deal closes, a Lender knows exactly what steps are required to review and process a draw, but they have no way to pre-define and automate them.</p>
              <p className='internal-case-study-body'>Financial institutions have a Global Credit Policy that every deal must adhere to, but there may be nuances to every deal that slightly adjust that policy, making it hard to manage one central workflow across an entire organization.</p>
              <p className='internal-case-study-body'>In our legacy product, all draws went through a fixed, Built defined workflow. The Built standard statuses aren’t 1:1 with financial institution internal terminology and steps. Causing confusion among Deal teams on what stage in their process a draw was currently in.</p>
              <img src={workflowcompare} alt="Workflow Comparison" style={{ width: '100%', height: 'auto', marginTop: '32px' }} />
            </div>
        </div>

            <div className="margin-top-72 section-content">
              <span className="info-label">Technical Definitions</span>
              <h2>Key components of a workflow builder and rules engine.</h2>
            </div>

        <div className="case-study-section-slim margin-top-48">
          <div className="two-column-layout">
            <div className="column">
              <img src={workflowicon} alt="workflow Icon" className="content-icon" />
              <h3 className="content-heading">Workflow & Statuses</h3>
              <p className="internal-case-study-body">Built around a single business object. Made up of status nodes that represent a status that can be applied to the given business object. Statuses can be user-defined. </p>
            </div>
            <div className="column">
              <img src={arrowsicon} alt="arrows Icon" className="content-icon" />
              <h3 className="content-heading">Transition</h3>
              <p className="internal-case-study-body">The connection between two status nodes. Defines which conditions govern a workflow’s ability to move from one status to another and what actions take place once a transition occurs.</p>
            </div>
          </div>
        </div>

        <div className="case-study-section-slim margin-top-48">
          <div className="two-column-layout">
            <div className="column">
              <img src={checks} alt="checks Icon" className="content-icon" />
              <h3 className="content-heading">Condition</h3>
              <p className="internal-case-study-body">A rule that governs whether a transition is able to move forward.</p>
            </div>
            <div className="column">
              <img src={bolt} alt="bolt Icon" className="content-icon" />
              <h3 className="content-heading">Action</h3>
              <p className="internal-case-study-body">Represents an action or task that the workflow will do on behalf of a user as a part of a transition. </p>
            </div>
          </div>
        </div>

        <div className="margin-top-72 image-container" style={{ width: '100%' }}>
          <img src={emptyworkflow} alt="Case Study 2" style={{ width: '100%', height: 'auto' }} />
        </div>

        <div className="margin-top-72 section-content">
              <span className="info-label">Solution</span>
              <h2>Extract Data</h2>
              <h3>WYSIWYG</h3>
              <p className="internal-case-study-body">WYSIWYG tool that allows both internal users (implementations) and external users (customer system administrators) to drag and drop status nodes to create a workflow.</p>
              <h3>Custom Statuses</h3>
              <p className="internal-case-study-body">Users are also able to create custom statuses built on top of standard canonical status types for consistency across the Built application.</p>
        </div>

        <div className="margin-top-72 image-container" style={{ width: '100%'}}>
          <img src={managestatus} alt="Case Study 2" style={{ width: '100%', height: 'auto' }} />
        </div>

        <div className="margin-top-72 section-content">
              <span className="info-label">Solution</span>
              <h2>Rules Engine</h2>
              <h3>Drag and Drop</h3>
              <p className="internal-case-study-body">Conditions can be dragged into a transition to enforce certain rules or requirements must be met in order for to workflow to progress.</p>
              <h3>Automated</h3>
              <p className="internal-case-study-body">Users can setup automated rules so the system will automatically check the presence of a document or that a value is within a certain threshold, reducing manual tasks for a Loan Administrator. </p>
        </div>

        <div className="margin-top-72 image-container" style={{ width: '100%'}}>
          <img src={conditions} alt="Case Study 2" style={{ width: '100%', height: 'auto' }} />
        </div>

        <div className="margin-top-72 section-content">
              <span className="info-label">Solution</span>
              <h2>Automated Actions</h2>
              <h3>Common Library of Actions</h3>
              <p className="internal-case-study-body">Used to execute various tasks such as sending an email, adding an approver to a Draw, or ordering a Vendor service, like an Inspection when a Draw reaches a specific point in a Workflow. It’s built on a common library where each action can be reused across all workflows.</p>
        </div>

        <div className="margin-top-72 image-container" style={{ width: '100%'}}>
          <img src={actions} alt="Case Study 2" style={{ width: '100%', height: 'auto' }} />
        </div>

        <div className="margin-top-72 image-container" style={{ width: '100%'}}>
          <img src={overview} alt="Case Study 2" style={{ width: '100%', height: 'auto' }} />
        </div>

        <div className="margin-top-72 image-container" style={{ width: '100%'}}>
          <img src={dialog} alt="Case Study 2" style={{ width: '100%', height: 'auto' }} />
        </div>
      </div>
    </div>
  );
}

export default CaseStudy2;

